import './styles.scss';

import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { AnimatedTransition } from '../../../../../components/animated-transition';
import { BackgroundMessage } from '../../../../../components/background-message';
import { CalendarInteractive } from '../../../../../components/calendar-interactive/calendar-interactive';
import { ReportFilterButtons } from '../../../../../components/report-filter-buttons';
import { PORTFOLIO_TABS_NAME } from '../../../../../constants/reports/reports-data';
import { RISK_TOKEN_LIST, VENUE_TYPE_LIST } from '../../../../../constants/reports/reports-filters';
import { PORTFOLIO_MESSAGES } from '../../../../../constants/reports/reports-messages';
import { GlobalContext } from '../../../../../providers/global-provider';
import { PNLContext } from '../../../../../providers/pnl-provider';
import {
  DATA_DISPLAY_KEYS,
  PortfolioManagementContext,
} from '../../../../../providers/portfolio-management';
import { IAccountOption } from '../../../../../providers/venue-provider';
import Utils from '../../../../../utils/immersive_utils';

import { LegacyPnlTable } from './legacy-pnl-table';

interface LegacyPnlBodyProps {
  accountOption: IAccountOption;
}

export const LegacyPnlBody = ({ accountOption }: LegacyPnlBodyProps) => {
  const { formatNumber } = Utils;

  const [errorPNL, setErrorPNL] = useState(false);
  const [lastUpdatePNL, setLastUpdatePNL] = useState('');

  const { portfolioCurrentTab } = useContext(GlobalContext);
  const {
    legacyPnlData: data,
    setLegacyPnlData: setData,
    getDataPnlLegacy,
    isLoadingPnl,
    filters: { pnlRiskToken, setPnlRiskToken, pnlVenue, setPnlVenue },
  } = useContext(PNLContext);
  const { aggregatedData, getDataFromAccount, selectedDataDisplayOption } = useContext(
    PortfolioManagementContext,
  );

  const isDataDisplayPerAccount = selectedDataDisplayOption === DATA_DISPLAY_KEYS.perAccount;

  const sumArrays = (arr1: any[], arr2: any[]) => {
    return arr1.map((value, index) => {
      return (value || 0) + (arr2[index] || 0);
    });
  };

  const groupedData = useMemo(() => {
    if (portfolioCurrentTab !== PORTFOLIO_TABS_NAME.pnlLegacy) {
      return {
        headers: [],
        values: [],
        newHeader: [],
      };
    }

    const newData = {
      headers: [],
      values: [],
      newHeader: [],
    };

    if (data) {
      const headers = Object.keys(data['display_mapping']);
      const processValues = (obj: any) => {
        return Object.values(data['display_mapping']).map((h: any) => {
          return obj[h] === undefined ? <div> &nbsp;</div> : obj[h];
        });
      };

      const { explain } = data;

      const tokenNode = Object.keys(explain).map((tokenKey: any) => {
        const handleChildren: any = (prefix: string, obj: any) => {
          const LABELS: any = {
            inventory: 'Inventory',
            new_trades: 'NEW TRADES PNL',
            options: 'Options',
            futures: 'Futures',
            token: 'Token',
            spot: 'Spot',
          };

          const objKeys = Object.keys(obj).filter((key: any) => key !== 'total');

          const nArr = objKeys.map((keys) => {
            const childrenObj = obj[keys];

            if (childrenObj.total) {
              const childrenHasPosition = !!Object.keys(childrenObj).find((key: any) => {
                return key !== 'total' && !!childrenObj[key]?.position;
              });

              return {
                id: `${prefix}-${keys}`,
                label: LABELS[keys] || keys,
                values: processValues(childrenObj['total'] || childrenObj),
                position: childrenHasPosition && 'position',
                children: handleChildren(`${prefix}-${keys}`, childrenObj),
              };
            }

            return {
              id: `${prefix}-${keys}`,
              label: LABELS[keys] || keys,
              position: childrenObj?.position,
              values: processValues(childrenObj['total'] || childrenObj),
            };
          });

          return nArr;
        };

        const token = explain[tokenKey];
        const tokenNode = handleChildren(tokenKey, token);

        const inventory = tokenNode.find((node: any) => node.label === 'Inventory');
        const newTrades = tokenNode.find((node: any) => node.label === 'NEW TRADES PNL');

        const summedValues = sumArrays(
          inventory ? inventory.values.map((value: any) => parseFloat(value) || 0) : [],
          newTrades ? newTrades.values.map((value: any) => parseFloat(value) || 0) : [],
        );

        const totalsValues = () => {
          return summedValues.map((value: number) => {
            const isNegative = !isNaN(value) && value < 0;
            const absoluteValue = Math.abs(value);
            const className = `total-value${isNegative ? ' negative' : ''}`;

            const decimalPlaces = pnlRiskToken === 'BASE' ? 6 : 2;

            const formattedValue = formatNumber(absoluteValue, decimalPlaces);

            return (
              <div key={value} className={className}>
                {isNegative ? `(${formattedValue})` : formattedValue}
              </div>
            );
          });
        };

        return {
          id: tokenKey,
          label: tokenKey,
          values: totalsValues(),
          children: tokenNode,
        };
      });

      const newHeader = Object.entries(data['display_mapping']).map(([key, value], index) => {
        const obj = {
          id: index,
          key: value,
          label: key,
          items: data['display'][key],
          hasItems: data['display'] !== undefined,
        };

        return obj;
      });

      return {
        headers,
        values: [tokenNode],
        newHeader: newHeader,
      };
    }

    return newData;
  }, [data]);

  const renderFilters = () => {
    if (data) {
      return (
        <div className="pnl-filters">
          <div className="pnl-filters-date">
            <CalendarInteractive onApply={() => {}} disabled />
          </div>
          <ReportFilterButtons
            titleFilter="Risk Token"
            dataArr={RISK_TOKEN_LIST}
            selected={pnlRiskToken}
            setSelected={setPnlRiskToken}
          />

          {!isDataDisplayPerAccount && (
            <ReportFilterButtons
              titleFilter="Venue"
              dataArr={VENUE_TYPE_LIST}
              selected={pnlVenue}
              setSelected={setPnlVenue}
            />
          )}
        </div>
      );
    }
  };

  useEffect(() => {
    if (
      isLoadingPnl ||
      !isDataDisplayPerAccount ||
      portfolioCurrentTab !== PORTFOLIO_TABS_NAME.pnlLegacy
    )
      return;

    const dataParent = PORTFOLIO_TABS_NAME.pnlLegacy;
    const pnlRiskTokenLowerCase = pnlRiskToken.toLowerCase();

    const dataAccount = getDataFromAccount(dataParent, accountOption, pnlRiskTokenLowerCase);

    if (!dataAccount) {
      setErrorPNL(false);
      setData(null);
      getDataPnlLegacy(accountOption, pnlRiskTokenLowerCase);
      return;
    }

    if (dataAccount.error) {
      setErrorPNL(true);
      setData(null);
      return;
    }

    if (dataAccount.data) {
      setData(dataAccount.data);
      setLastUpdatePNL(dataAccount.lastUpdate);
      setErrorPNL(false);
      return;
    }

    setData(null);
    setErrorPNL(false);
  }, [accountOption, isLoadingPnl, pnlRiskToken, portfolioCurrentTab, isDataDisplayPerAccount]);

  const callPnlAggregatedAccounts = useCallback(() => {
    const AggregatedAccountsFilteredByExchangeDeribit = aggregatedData.accounts.filter(
      (account) => account.exchange === 'deribit',
    );
    const AggregatedAccountsFilteredByExchangeOkx = aggregatedData.accounts.filter(
      (account) => account.exchange === 'okx',
    );

    const pnlRiskTokenLowerCase = pnlRiskToken.toLowerCase();

    if (AggregatedAccountsFilteredByExchangeDeribit.length > 0) {
      getDataPnlLegacy(
        AggregatedAccountsFilteredByExchangeDeribit,
        pnlRiskTokenLowerCase,
        'deribit',
      );
    }
    if (AggregatedAccountsFilteredByExchangeOkx.length > 0) {
      getDataPnlLegacy(AggregatedAccountsFilteredByExchangeOkx, pnlRiskTokenLowerCase, 'okx');
    }
  }, [aggregatedData, pnlRiskToken]);

  if (!isDataDisplayPerAccount) {
    return <BackgroundMessage message={PORTFOLIO_MESSAGES.NO_AGGREGATED_ACCOUNTS} />;
  }

  if (isLoadingPnl) {
    return <BackgroundMessage isLoading />;
  }

  if (errorPNL) {
    const pnlRiskTokenLowerCase = pnlRiskToken.toLowerCase();

    return (
      <BackgroundMessage
        message={PORTFOLIO_MESSAGES.ERROR_REQUEST_PNL}
        type="error"
        button={{
          title: PORTFOLIO_MESSAGES.ERROR_REQUEST_PNL.button,
          onClick: () =>
            isDataDisplayPerAccount
              ? getDataPnlLegacy(accountOption, pnlRiskTokenLowerCase)
              : callPnlAggregatedAccounts(),
        }}
      />
    );
  }

  if (!data) {
    return <BackgroundMessage message={PORTFOLIO_MESSAGES.NO_DATA_FOUND} />;
  }

  return (
    <div className="legacy-pnl-body-container">
      <div className="options">{renderFilters()}</div>

      <AnimatedTransition className="legacy-pnl-content">
        <div className="data-table">
          <LegacyPnlTable
            data={groupedData.values}
            header={groupedData.headers}
            lastUpdatePNL={lastUpdatePNL}
            newHeader={groupedData.newHeader}
          />
        </div>
      </AnimatedTransition>
    </div>
  );
};
